import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Link } from 'react-router-dom';
import NavBar from './components/NavBar';
import ErrorBoundary from './components/ErrorBoundary';
import { ServiceType } from './types';

// Eager Load Home Page (Fixes LCP Score & Layout Shift)
import Home from './pages/Home';

// Use Deferred Loader for 3D Background
import DeferredBackground from './components/DeferredBackground';

// Lazy Load Secondary Pages (Code Splitting)
const Security = React.lazy(() => import('./pages/Security'));
const WebDev = React.lazy(() => import('./pages/WebDev'));
const StrategyDoc = React.lazy(() => import('./pages/StrategyDoc'));
const About = React.lazy(() => import('./pages/About'));
const Handoff = React.lazy(() => import('./pages/Handoff'));
const Contact = React.lazy(() => import('./pages/Contact'));
const Privacy = React.lazy(() => import('./pages/Privacy'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

// Minimal Status Indicator (Bottom Left) instead of full HUD
const StatusIndicator: React.FC<{ activeService: ServiceType }> = ({ activeService }) => {
    return (
        <div className="fixed bottom-8 left-8 z-30 font-mono text-[10px] uppercase text-gray-500 hidden md:block pointer-events-none select-none">
             <div className="flex items-center gap-3 bg-black/40 backdrop-blur-sm px-4 py-2 rounded-full border border-white/5">
                <div className={`w-2 h-2 rounded-full animate-pulse ${
                    activeService === ServiceType.SECURITY ? 'bg-pinaka-cyan' :
                    activeService === ServiceType.WEB_DEV ? 'bg-pinaka-violet' : 'bg-emerald-500'
                }`}></div>
                <span>CONTEXT: {activeService === ServiceType.NONE ? 'GLOBAL' : activeService}</span>
            </div>
        </div>
    );
};

const GlobalFooter = () => (
    <footer className="fixed bottom-0 w-full py-2 text-center z-30 pointer-events-none">
        <div className="inline-flex gap-4 text-[10px] text-gray-400 font-mono uppercase bg-black/80 backdrop-blur px-4 py-1 rounded-t-lg pointer-events-auto">
            <span>© {new Date().getFullYear()} Pinaka</span>
            <span className="text-gray-600">|</span>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
        </div>
    </footer>
);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const PageFallback = () => (
    <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-xs font-mono text-gray-600 uppercase tracking-widest animate-pulse">
            Initializing Secure Connection...
        </div>
    </div>
);

const App: React.FC = () => {
  const [currentContext, setCurrentContext] = useState<ServiceType>(ServiceType.NONE);

  return (
    <Router>
      <ScrollToTop />
      <div 
        className="relative min-h-screen bg-pinaka-dark text-pinaka-text font-sans selection:bg-pinaka-violet/30 selection:text-white"
        style={{ backgroundColor: '#050505' }}
      >
        
        {/* Visual Texture - Extremely subtle now */}
        <div className="noise"></div>
        
        {/* 3D Background Layer Wrapped in Error Boundary for Safety */}
        <ErrorBoundary>
             {/* DeferredLoader handles Suspense internally */}
             <DeferredBackground activeService={currentContext} />
        </ErrorBoundary>
        
        {/* Navigation */}
        <NavBar />

        {/* Main Content Area */}
        <main className="relative z-20 pt-20 pb-8">
          <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route path="/" element={<Home setContext={setCurrentContext} />} />
                <Route path="/security" element={<Security setContext={setCurrentContext} />} />
                <Route path="/web-dev" element={<WebDev setContext={setCurrentContext} />} />
                <Route path="/strategy" element={<StrategyDoc setContext={setCurrentContext} />} />
                <Route path="/about" element={<About setContext={setCurrentContext} />} />
                {/* Note: Handoff route intentionally kept active for demo but removed from Nav */}
                <Route path="/handoff" element={<Handoff setContext={setCurrentContext} />} />
                <Route path="/contact" element={<Contact setContext={setCurrentContext} />} />
                <Route path="/privacy" element={<Privacy setContext={setCurrentContext} />} />
                <Route path="*" element={<NotFound setContext={setCurrentContext} />} />
              </Routes>
          </Suspense>
        </main>

        {/* Status Indicator */}
        <StatusIndicator activeService={currentContext} />
        
        {/* Global Legal Footer */}
        <GlobalFooter />

      </div>
    </Router>
  );
};

export default App;